import { ENDPOINT } from '~/constants/endpoints';

import { type KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { withoutObjectKeysWhereValueIs } from '@vestigas/utils';

import {
  type SupplierIdentifierCheckParams,
  type SupplierIdentifierCheckResponse,
} from './types';
import {
  zodSupplierIdentifierCheckParamsSchema,
  zodSupplierIdentifierCheckResponseSchema,
} from './validations';

/**
 * Checks whether a supplier identifier (VAT ID, HRA, HRB, or GLN) exists as
 * a connected supplier, a blacklisted company, or a pending request.
 *
 * @param params - The recipient company and the identifiers to check. Provide
 *   at least one identifier for a meaningful result.
 * @param options - Additional request options.
 * @returns The existence-check outcome, plus the matched company or existing
 *   request when the identifier is already known.
 * @throws VestigasHTTPError if the API request fails.
 * @see https://app.dev.vestigas.com/redoc#tag/Suppliers/operation/check_by_identifier_suppliers_requests_check_get
 */
export const fetchSupplierIdentifierCheck = async (
  params: SupplierIdentifierCheckParams,
  options?: KyRequestOptions,
) => {
  return vestigasApi
    .get(ENDPOINT.SUPPLIER.CHECK_IDENTIFIER(), {
      searchParams: withoutObjectKeysWhereValueIs(params, [
        '',
        null,
        undefined,
      ]) as Record<string, string>,
      ...options,
      validate: {
        ...options?.validate,
        searchParams: zodSupplierIdentifierCheckParamsSchema,
        response: zodSupplierIdentifierCheckResponseSchema,
      },
    })
    .json<SupplierIdentifierCheckResponse>();
};
